import React from 'react';
import { X, Minus, Plus, ShoppingBag } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const CartSidebar = ({ isOpen, onClose }) => {
  const { cart, updateQuantity, removeFromCart, getCartTotal, getCartCount, getDiscount, getDeliveryFee } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  if (!isOpen) return null;

  const subtotal = getCartTotal();
  const discount = getDiscount();
  const discountAmount = discount.amount;
  const afterDiscount = subtotal - discountAmount;
  const deliveryFee = getDeliveryFee();
  const total = afterDiscount + deliveryFee;
  const totalQuantity = getCartCount();

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
        data-testid="cart-overlay"
      />

      {/* Sidebar */}
      <div
        className="fixed right-0 top-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 transform transition-transform duration-300 flex flex-col"
        data-testid="cart-sidebar"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#a80707' }}>
              <ShoppingBag className="h-6 w-6" />
              Your Cart
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              data-testid="close-cart-btn"
            >
              <X className="h-6 w-6" style={{ color: '#a80707' }} />
            </button>
          </div>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cart.length === 0 ? (
            <div className="text-center py-12" data-testid="empty-cart-message">
              <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">Your cart is empty</p>
              <p className="text-gray-400 text-sm mt-2">Add some delicious meals!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((item) => (
                <div
                  key={item.id}
                  className="cart-item flex gap-4 p-4 rounded-lg border border-gray-200"
                  data-testid={`cart-item-${item.id}`}
                >
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">{item.name}</h3>
                    <p className="text-sm text-gray-600 mb-2">CA${item.price.toFixed(2)}</p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="quantity-btn p-1 rounded-full border-2"
                        style={{ borderColor: '#a80707' }}
                        data-testid={`decrease-qty-${item.id}`}
                      >
                        <Minus className="h-4 w-4" style={{ color: '#a80707' }} />
                      </button>
                      <span className="w-8 text-center font-semibold" data-testid={`item-quantity-${item.id}`}>{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="quantity-btn p-1 rounded-full border-2"
                        style={{ borderColor: '#a80707' }}
                        data-testid={`increase-qty-${item.id}`}
                      >
                        <Plus className="h-4 w-4" style={{ color: '#a80707' }} />
                      </button>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="ml-auto hover:opacity-70 text-sm font-medium"
                        style={{ color: '#a80707' }}
                        data-testid={`remove-item-${item.id}`}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="border-t border-gray-200 p-6 space-y-4">
            {/* Delivery Fee Notice */}
            {deliveryFee > 0 && (
              <div className="rounded-lg p-3 text-sm" style={{ backgroundColor: '#fff3cd' }}>
                <p className="font-semibold" style={{ color: '#856404' }}>
                  🚚 Add {10 - totalQuantity} more plate{10 - totalQuantity > 1 ? 's' : ''} to get FREE delivery!
                </p>
              </div>
            )}
            
            {deliveryFee === 0 && totalQuantity >= 10 && (
              <div className="rounded-lg p-3 text-sm" style={{ backgroundColor: '#d4edda' }}>
                <p className="font-semibold" style={{ color: '#155724' }}>
                  🎉 FREE delivery on your order!
                </p>
              </div>
            )}

            {/* Discount Progress */}
            {discount.nextTier && (
              <div className="rounded-lg p-3 text-sm" style={{ backgroundColor: '#f9f7ed' }}>
                <p className="font-semibold" style={{ color: '#a80707' }}>
                  🎯 Add {discount.itemsToNextTier} more plate{discount.itemsToNextTier > 1 ? 's' : ''} to unlock {discount.nextTier >= 25 ? '20' : discount.nextTier >= 15 ? '15' : discount.nextTier >= 10 ? '10' : '5'}% discount!
                </p>
              </div>
            )}
            
            {discount.percentage === 20 && !discount.nextTier && (
              <div className="rounded-lg p-3 text-sm" style={{ backgroundColor: '#f9f7ed' }}>
                <p className="font-semibold" style={{ color: '#a80707' }}>
                  🎉 Maximum 20% discount unlocked! You're saving big!
                </p>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal ({totalQuantity} plates)</span>
                <span data-testid="cart-subtotal">CA${subtotal.toFixed(2)}</span>
              </div>
              
              {discount.percentage > 0 && (
                <div className="flex justify-between text-sm font-semibold" style={{ color: '#a80707' }}>
                  <span>Discount ({discount.percentage}%)</span>
                  <span data-testid="cart-discount">-CA${discountAmount.toFixed(2)}</span>
                </div>
              )}
              
              {deliveryFee > 0 && (
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Delivery Fee</span>
                  <span data-testid="cart-delivery">CA${deliveryFee.toFixed(2)}</span>
                </div>
              )}
              
              <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t border-gray-200">
                <span>Total</span>
                <span data-testid="cart-total">CA${total.toFixed(2)}</span>
              </div>
            </div>
            <Button
              onClick={handleCheckout}
              className="w-full checkout-btn text-white font-semibold py-6 text-lg"
              style={{ backgroundColor: '#a80707' }}
              data-testid="checkout-btn"
            >
              Proceed to Checkout
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartSidebar;